import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

const API = '/api/google-vision-settings';

export interface GoogleVisionTestResult {
  ok: boolean;
  connected_at?: string | null;
  message?: string;
  error?: string;
}

async function authHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

export function useGoogleVision() {
  const [connectedAt, setConnectedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<GoogleVisionTestResult | null>(null);

  const loadStatus = useCallback(async (betrieb_id: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: HeadersInit = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      const res = await fetch(`${API}?betrieb_id=${encodeURIComponent(betrieb_id)}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setConnectedAt(data.connected_at ?? null);
    } catch {
      // silent — no connectivity, show as not connected
    }
  }, []);

  const verbinden = useCallback(async (betrieb_id: string, jsonKeyContent: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'verbinden', betrieb_id, jsonKeyContent }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        return false;
      }
      setConnectedAt(data.connected_at ?? new Date().toISOString());
      return true;
    } catch (err: any) {
      setError(err.message || 'Verbindung fehlgeschlagen');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const testen = useCallback(async (betrieb_id: string): Promise<GoogleVisionTestResult | null> => {
    setLoading(true);
    setError(null);
    setTestResult(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'testen', betrieb_id }),
      });
      const data: GoogleVisionTestResult = await res.json();
      if (data.error) {
        setError(data.error);
        setTestResult({ ok: false, error: data.error });
        return data;
      }
      setTestResult(data);
      return data;
    } catch (err: any) {
      const errResult: GoogleVisionTestResult = { ok: false, error: err.message || 'Test fehlgeschlagen' };
      setError(errResult.error!);
      setTestResult(errResult);
      return errResult;
    } finally {
      setLoading(false);
    }
  }, []);

  const trennen = useCallback(async (betrieb_id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ action: 'trennen', betrieb_id }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        return false;
      }
      setConnectedAt(null);
      setTestResult(null);
      return true;
    } catch (err: any) {
      setError(err.message || 'Trennen fehlgeschlagen');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    connectedAt,
    loading,
    error,
    testResult,
    loadStatus,
    verbinden,
    testen,
    trennen,
  };
}
